import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Button } from '@design-system/components/atoms/Button';
import { authService } from '@features/authentication/services/auth.service';
import { APP_ROUTES } from '@shared/constants/routes.constants';

export interface VerifyCodeFormProps {
  email?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const VerifyCodeForm: React.FC<VerifyCodeFormProps> = ({
  email: emailProp,
  onSuccess,
  onError,
}) => {
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [serverError, setServerError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Get email and token from props or location state (passed from ForgotPasswordForm)
  const locationState = location.state as { email?: string; otpSessionToken?: string } | null;
  const emailFromState = locationState?.email;
  const tokenFromState = locationState?.otpSessionToken;

  // Validate that navigation came from forgot-password page with valid token
  const storedToken = sessionStorage.getItem('otp_session_token');
  const storedEmail = sessionStorage.getItem('otp_session_email');

  const isValidNavigation =
    emailProp ??
    (emailFromState &&
      tokenFromState &&
      storedToken === tokenFromState &&
      storedEmail === emailFromState);

  const email = emailProp ?? emailFromState;

  // Redirect to forgot-password if navigation is not valid
  // This prevents accessing verify-code directly via URL or browser history
  useEffect(() => {
    if (!isValidNavigation) {
      // Clear any stale session data
      sessionStorage.removeItem('otp_session_token');
      sessionStorage.removeItem('otp_session_email');
      void navigate(APP_ROUTES.FORGOT_PASSWORD, { replace: true });
    }
  }, [isValidNavigation, navigate]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => {
        clearInterval(interval);
      };
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...code];
    pastedData.split('').forEach((char, index) => {
      if (index < 6) {
        newCode[index] = char;
      }
    });
    setCode(newCode);

    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');
    setIsLoading(true);

    try {
      const fullCode = code.join('');
      if (fullCode.length !== 6) {
        throw new Error('Por favor, insira o código completo');
      }

      if (!email) {
        throw new Error('Email não encontrado. Tente novamente.');
      }

      // Verify OTP code via API
      await authService.verifyOtp(email, fullCode);

      // Store verified email and OTP code for reset password page
      sessionStorage.setItem('otp_verified_email', email);
      sessionStorage.setItem('otp_verified_code', fullCode);

      onSuccess?.();
      void navigate(APP_ROUTES.RESET_PASSWORD);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Código inválido. Tente novamente.';

      setServerError(errorMessage);
      onError?.(error instanceof Error ? error : new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || !email) return;

    setIsResending(true);
    setServerError('');
    try {
      await authService.generateOtp(email);

      setTimer(60);
      setCanResend(false);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erro ao reenviar código. Tente novamente.';
      setServerError(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  const handleResendCode = () => {
    void handleResend();
  };

  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, '$1********$3')
    : '********@*****.com.br';
  const isCodeComplete = code.every((digit) => digit !== '');

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(e);
      }}
      className="flex flex-col gap-4"
    >
      <p className="text-neutral-600 text-base mb-6 text-center">
        Favor inserir o código de recuperação enviado para o e-mail{' '}
        <span className="font-semibold">{maskedEmail}</span>
      </p>

      <div className="flex justify-center gap-2 mb-4">
        {code.map((digit, index) => {
          const digitIndex = index + 1;
          const inputId = `code-input-${digitIndex.toString()}-${digit}`;
          return (
            <input
              key={inputId}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => {
                handleChange(index, e.target.value);
              }}
              onKeyDown={(e) => {
                handleKeyDown(index, e);
              }}
              onPaste={index === 0 ? handlePaste : undefined}
              className="w-12 h-14 text-center text-xl font-semibold border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
              aria-label={`Dígito ${digitIndex.toString()}`}
            />
          );
        })}
      </div>

      <div className="text-center mb-2">
        {!canResend ? (
          <p className="text-neutral-500 text-sm">
            Aguarde {timer.toString()} segundos para reenviar o código
          </p>
        ) : (
          <button
            type="button"
            onClick={handleResendCode}
            className="text-lg text-purple-600 font-bold hover:text-purple-700 hover:underline transition-colors disabled:opacity-50"
            disabled={isLoading || isResending}
          >
            {isResending ? 'Reenviando...' : 'Reenviar código de confirmação'}
          </button>
        )}
      </div>

      {serverError && (
        <div
          className="p-3 rounded-lg bg-danger-50 border border-danger-200 text-danger-700 text-sm"
          role="alert"
        >
          {serverError}
        </div>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        loading={isLoading}
        disabled={isLoading || !isCodeComplete}
      >
        {isLoading ? 'Confirmando...' : 'Confirmar'}
      </Button>

      <Button
        type="button"
        variant="outline"
        size="lg"
        fullWidth
        onClick={() => void navigate(APP_ROUTES.FORGOT_PASSWORD)}
        disabled={isLoading}
      >
        Voltar
      </Button>
    </form>
  );
};
